import { useMemo } from 'react';
// eslint-disable-next-line @direct-frontend/modules/slice-boundaries -- slug категорий/коллекций нужен для search payload
import { useLoadMenuInternalLinks } from '#entities/menu';
import { type OpenApiGetLightProductCardsByRequestRequest } from '#shared/api/schema';
import { DEFAULT_PAGE } from '#shared/components/grid';
import { useLightProductCards } from '../api';
import { mapProductCardForGallery, type ProductCardForGallery } from '../components/CardsGalleryItem';
import {
    ADDITIONAL_PRODUCTS_GALLERY_MAX_CARDS,
    ADDITIONAL_PRODUCTS_MANUAL_MODE,
    ADDITIONAL_PRODUCTS_SOURCE_MODE,
    type AdditionalProductsFormValue,
    getAdditionalProductsSearchPayload,
} from '../lib/additionalProducts';

type UseAdditionalProductsGalleryParams = {
    cardId: string;
    formValue: AdditionalProductsFormValue;
    enabled?: boolean;
};

export function useAdditionalProductsGallery({
    cardId,
    formValue,
    enabled = true,
}: UseAdditionalProductsGalleryParams) {
    const { categories, collections } = useLoadMenuInternalLinks();

    const categorySlugs = useMemo(
        () =>
            formValue.binding.categoryIds
                .map((id) => categories.find((category) => category.id === id)?.slug)
                .filter((slug): slug is string => Boolean(slug)),
        [categories, formValue.binding.categoryIds]
    );

    const collectionSlugs = useMemo(
        () =>
            formValue.binding.collectionIds
                .map((id) => collections.find((collection) => collection.id === id)?.slug)
                .filter((slug): slug is string => Boolean(slug)),
        [collections, formValue.binding.collectionIds]
    );

    const searchPayload = useMemo(
        () =>
            getAdditionalProductsSearchPayload({
                cardId,
                formValue,
                categorySlugs,
                collectionSlugs,
            }),
        [cardId, categorySlugs, collectionSlugs, formValue]
    );

    const skip =
        !enabled ||
        !searchPayload ||
        (formValue.sourceMode === ADDITIONAL_PRODUCTS_SOURCE_MODE.MANUAL &&
            formValue.manualMode === ADDITIONAL_PRODUCTS_MANUAL_MODE.CATEGORIES &&
            categorySlugs.length === 0 &&
            collectionSlugs.length === 0) ||
        (formValue.sourceMode === ADDITIONAL_PRODUCTS_SOURCE_MODE.MANUAL &&
            formValue.manualMode === ADDITIONAL_PRODUCTS_MANUAL_MODE.SELECTED &&
            !formValue.binding.productListId);

    const payload = useMemo<OpenApiGetLightProductCardsByRequestRequest>(() => {
        if (!searchPayload) {
            return {
                page: DEFAULT_PAGE,
                per_page: ADDITIONAL_PRODUCTS_GALLERY_MAX_CARDS,
            };
        }

        return {
            ...searchPayload,
            page: DEFAULT_PAGE,
        };
    }, [searchPayload]);

    const { data, isFetching } = useLightProductCards(
        {
            payload,
        },
        { skip }
    );

    const items = useMemo<ProductCardForGallery[]>(
        () => (data?.cards ?? []).map(mapProductCardForGallery),
        [data?.cards]
    );

    const isAiEmpty = formValue.sourceMode === ADDITIONAL_PRODUCTS_SOURCE_MODE.AI && !isFetching && items.length === 0;

    return {
        items,
        totalCards: data?.total_count ?? 0,
        isFetching,
        isAiEmpty,
        searchPayload,
    };
}
